import * as React from 'react';
import { Camera, CameraOff, Circle, StopCircle } from 'lucide-react';

export interface VideoRecordingResult {
  blob: Blob;
  durationMs: number;
}

interface LiveVideoCaptureProps {
  apiBase: string;
  authToken: string | null;
  /** Called when a recording blob is ready to upload. Returns the S3 asset_id. */
  onRecordingReady?: (result: VideoRecordingResult) => void;
  onError?: (msg: string) => void;
}

export function LiveVideoCapture({ onRecordingReady, onError }: LiveVideoCaptureProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const recorderRef = React.useRef<MediaRecorder | null>(null);
  const chunksRef = React.useRef<Blob[]>([]);
  const startedAtRef = React.useRef<number>(0);

  const [cameraOn, setCameraOn] = React.useState(false);
  const [recording, setRecording] = React.useState(false);
  const [hasRecording, setHasRecording] = React.useState(false);
  const [permDenied, setPermDenied] = React.useState(false);

  const startCamera = React.useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false, // ARIA widget handles audio separately
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraOn(true);
      setPermDenied(false);
    } catch (e: any) {
      if (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError') {
        setPermDenied(true);
      }
      onError?.(e?.message ?? 'Camera access denied');
    }
  }, [onError]);

  const stopCamera = React.useCallback(() => {
    if (recording) stopRecording();
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
  }, [recording]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRecording = React.useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    startedAtRef.current = Date.now();

    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
      ? 'video/webm'
      : 'video/mp4';

    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setHasRecording(true);
      onRecordingReady?.({ blob, durationMs: Date.now() - startedAtRef.current });
    };
    recorder.start(1000); // 1s chunks
    recorderRef.current = recorder;
    setRecording(true);
  }, [onRecordingReady]);

  const stopRecording = React.useCallback(() => {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  }, []);

  // Auto-cleanup on unmount
  React.useEffect(() => {
    return () => {
      recorderRef.current?.stop();
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 bg-black">
      {/* Video preview */}
      <div className="relative aspect-video bg-black/80">
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          className={`w-full h-full object-cover transition-opacity ${cameraOn ? 'opacity-100' : 'opacity-0'}`}
        />
        {!cameraOn && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/30">
            <CameraOff className="h-8 w-8" strokeWidth={1.5} />
            <p className="text-xs font-mono">Camera off</p>
            {permDenied && (
              <p className="text-xs text-rose-400/80 text-center max-w-[200px] leading-relaxed">
                Camera access was denied. Enable it in browser settings.
              </p>
            )}
          </div>
        )}
        {recording && (
          <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/70 rounded-full px-2.5 py-1">
            <Circle className="h-2 w-2 fill-red-500 text-red-500 animate-pulse" />
            <span className="text-[10px] font-mono text-red-400">REC</span>
          </div>
        )}
        {hasRecording && !recording && (
          <div className="absolute top-3 right-3 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-2.5 py-1">
            <span className="text-[10px] font-mono text-emerald-400">Saved</span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-3 flex items-center gap-2 border-t border-white/8">
        {!cameraOn ? (
          <button
            onClick={startCamera}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/8 border border-white/12 text-sm text-white/70 hover:border-white/25 hover:text-white transition text-xs"
          >
            <Camera className="h-3.5 w-3.5" /> Enable camera
          </button>
        ) : (
          <>
            {!recording ? (
              <button
                onClick={startRecording}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/25 text-red-400 hover:bg-red-500/15 transition text-xs"
              >
                <Circle className="h-3 w-3 fill-red-500 text-red-500" /> Start recording
              </button>
            ) : (
              <button
                onClick={stopRecording}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/8 border border-white/12 text-white/70 hover:border-white/25 transition text-xs"
              >
                <StopCircle className="h-3.5 w-3.5" /> Stop recording
              </button>
            )}
            <button
              onClick={stopCamera}
              className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/8 text-white/30 hover:border-white/15 hover:text-white/50 transition text-xs"
            >
              <CameraOff className="h-3 w-3" /> Off
            </button>
          </>
        )}
      </div>
    </div>
  );
}
